import { Component, forwardRef, OnInit } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import {
  DATES_COMPARE,
  CHART_FORMAT,
  Y_AXIS_FORMAT,
} from '../../../../../modules/dashboard/dashboard/widgets/charts/common/interfaces/chart.constant';
import { IChartSettings } from '../../../../../modules/dashboard/dashboard/widgets/charts/common/interfaces/chart-settings.interface';
import { DestroySubscribers } from '../../../../decorators/destroy-subscribers.decorator';
import { swapFormArray } from '../../../../helpers/swap-items';

@Component({
  templateUrl: './chart.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChartComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ChartComponent),
      multi: true,
    },
  ],
  styles: [
    `
      .group {
        border: 1px solid #e6e6e6;
        padding: 10px 20px;
        border-radius: var(--radius-large, 8px);
      }
    `,
  ],
})
@DestroySubscribers()
export class ChartComponent implements ControlValueAccessor, OnInit {
  public form = new ChartFormGroup<IChartGroup>({
    defaultId: new UntypedFormControl(null, Validators.required),
    charts: new ChartFormArray<ChartFormGroup<ICharts>>([]),
  });
  public terms: any[];
  public datesCompare = DATES_COMPARE;
  public chartFormats = CHART_FORMAT;
  public yAxisFormats = Y_AXIS_FORMAT;
  public chartIds = this.getDefaultData(this.chartArray);
  public subscribers: Record<string, Subscription> = {};
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};

  public localizations = new Map([
    ['Clicks', $localize`:@@common.clicks:Clicks`],
    ['Click and Conversion', $localize`:@@chart.click_and_conversion:Click and Conversion`],
    ['Click and Conv', $localize`:@@chart.click_and_conversion:Click and Conversion`],
  ]);

  public ngOnInit(): void {
    this.subscribers.formChanges = this.form.valueChanges.pipe(debounceTime(100)).subscribe((value: IChartSettings) => {
      value.charts = value.charts.map((item, i) => {
        item.id = i;
        return item;
      });
      value.defaultId = +value.defaultId;
      this.onChange(JSON.stringify(value));
      this.onTouch();
    });
  }

  public validate(): ValidationErrors | null {
    return this.form.invalid ? { errors: true } : null;
  }

  public swap(formArray: ChartFormArray<ChartFormGroup<ICharts>>, a: number, b: number): void {
    swapFormArray(formArray, a, b);
  }

  public pushFullForm(): void {
    this.pushForm();
    const i = this.chartArray.length - 1;
    this.pushTermsForm(i);
  }

  public pushTermsForm(i: number): void {
    this.chartArray.at(i).controls.terms.push(this.makeTermsForm());
  }

  public removeTermsForm(i: number, k: number): void {
    this.chartArray.at(i).controls.terms.removeAt(k);
  }

  public get chartArray(): ChartFormArray<ChartFormGroup<ICharts>> {
    return this.form.controls.charts;
  }

  public removeForm(i: number): void {
    this.form.controls.defaultId.reset();
    this.chartArray.removeAt(i);
  }

  public writeValue(obj: any): void {
    try {
      const chartSettings: IChartSettings = JSON.parse(obj);
      chartSettings.charts.forEach((chart) => {
        this.pushForm();
        const index = this.chartArray.length - 1;
        chart.terms.forEach(() => {
          this.pushTermsForm(index);
        });
      });
      this.form.patchValue(chartSettings);
    } catch {}
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public trackBy = (i: number) => i;

  private pushForm(): void {
    const form = this.makeGroupForm();
    this.chartArray.push(form);
  }

  private makeGroupForm(): ChartFormGroup<ICharts> {
    return new ChartFormGroup({
      title: new UntypedFormControl(null, [Validators.required, Validators.maxLength(20)]),
      id: new UntypedFormControl(),
      dateCompareKey: new UntypedFormControl(null),
      format: new UntypedFormControl(null, Validators.required),
      yAxisFormat: new UntypedFormControl(null, Validators.required),
      yAxisLabel: new UntypedFormControl(null, Validators.maxLength(20)),
      terms: new ChartFormArray([]),
    });
  }

  private makeTermsForm(): ChartFormGroup<IChartTerms> {
    return new ChartFormGroup({
      uuid: new UntypedFormControl(null, Validators.required),
      color: new UntypedFormControl('#F0560E', Validators.required),
      secondaryColor: new UntypedFormControl('#F0560E', Validators.required),
    });
  }
  private getDefaultData(ctr: AbstractControl): Observable<Record<string, string>> {
    return ctr.valueChanges.pipe(
      debounceTime(200),
      shareReplay(1),
      map((values: any[]) => values.reduce((acc, value, i) => ((acc[i] = value.title), acc), {}))
    );
  }
}

interface IChartGroup extends IControls {
  charts: ChartFormArray<ChartFormGroup<ICharts>>;
  defaultId: UntypedFormControl;
}

interface ICharts extends IControls {
  title: UntypedFormControl;
  id: UntypedFormControl;
  dateCompareKey: UntypedFormControl;
  format: UntypedFormControl;
  yAxisFormat: UntypedFormControl;
  yAxisLabel: UntypedFormControl;
  terms: ChartFormArray<ChartFormGroup<IChartTerms>>;
}

interface IChartTerms extends IControls {
  uuid: UntypedFormControl;
  color: UntypedFormControl;
  secondaryColor: UntypedFormControl;
}

class ChartFormArray<T extends AbstractControl> extends UntypedFormArray {
  controls: T[];

  constructor(
    controls: T[],
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
  }

  at(i: number): T {
    return super.at(i) as T;
  }

  push(control: T): void {
    super.push(control);
  }

  /**
   * TODO: remove timeout
   * (update selectData)
   */
  patchValue(value: any[]): void {
    super.patchValue(value, { onlySelf: false, emitEvent: true });
    setTimeout(() => {
      this.updateValueAndValidity();
    }, 100);
  }
}

class ChartFormGroup<T extends IControls> extends UntypedFormGroup {
  controls: T;
  selectData: Observable<Record<string, string>>;

  constructor(
    controls: T,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
  }
}

interface IControls {
  [key: string]: AbstractControl;
}
